import { scaleValueToZoom, scaleToZoom } from "./ZoomUtils";
import { gatherProps } from "./Merge/MergeUtils.mjs";

import AppConstants from "@/constants/AppConstants";
import { updateToolMatrix } from "./Transformer/tool";
import BlockUtils from "./BlockUtils";
import { positionElementToContainer } from "./GroupUtils";
import PROPERTY_MAPS from "@/constants/PropertyMaps";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 * @typedef {import("@/utils/ZoomUtils").WritableDOMRect} WritableDOMRect
 */

/**
 * Gets the area that contains all canvases (design area). Applies stage padding and zoom to more accurately represent the space.
 * @param {*} layout
 * @param {*} zoom
 * @returns {WritableDOMRect}
 */
export const getCanvasBounds = (layout, zoom = 1) => {
  const canvas = document.getElementById(`${layout}_stage_canvas`);
  if (!canvas) {
    throw new Error(`getCanvasBounds: #${layout}_stage_canvas not in DOM`);
  }
  const { x, y, width, height, top, left, bottom, right } =
    canvas.getBoundingClientRect();

  const scaledStagePadding = scaleValueToZoom(AppConstants.STAGE_PADDING, zoom);
  const canvasBounds = {
    x,
    y: y + scaledStagePadding,
    width,
    height: height - scaledStagePadding,
    top: top + scaledStagePadding,
    left,
    bottom,
    right,
  };

  return scaleToZoom(canvasBounds, zoom);
};

/**
 * Gets a dom rect for an area effectively the same as the total transformer svg area. Scales to zoom if supplied.
 * @param {LAYOUTS[number]} layout
 * @param {number} zoom
 * @returns {WritableDOMRect}
 */
const getInnerStageBounds = (layout, zoom = 1) => {
  const container = document.getElementById(`${layout}_stage_bg`);
  if (!container) {
    throw new Error(`getInnerStageBounds: #${layout}_stage_bg not in DOM`);
  }
  return scaleToZoom(container.getBoundingClientRect(), zoom);
};

/**
 * Gets an element's matrix and dimensions as though it were generated by the transformer.
 * @param {object} selectedElements
 * @param {LAYOUTS[number]} layout
 * @param {number} yOffset
 * @param {number=} zoom
 * @returns
 */
export const getSelectedElementFullMatrix = ({
  selectedElements,
  block,
  layout,
  yOffset,
  zoom = 1,
}) => {
  let dimensions = gatherProps(
    selectedElements[0],
    layout,
    PROPERTY_MAPS.ELEMENT_POSITION,
    false,
  );

  // offset child bounds to parent
  if (selectedElements[0]?.pId && block) {
    const parentContainer = BlockUtils.getChildParent(
      block,
      selectedElements[0],
    );
    const parentDimensions = gatherProps(
      parentContainer,
      layout,
      PROPERTY_MAPS.ELEMENT_POSITION,
      false,
    );
    dimensions = positionElementToContainer(
      dimensions,
      parentDimensions.toJS(),
      true,
    );
  }

  const containerBounds = getInnerStageBounds(layout, zoom);
  const canvasBounds = getCanvasBounds(layout, zoom);
  const pageOffsetX = canvasBounds.x - containerBounds.x;

  const matrix = updateToolMatrix(dimensions);
  matrix[4] += pageOffsetX;
  matrix[5] += yOffset;

  return {
    matrix,
    dimensions,
    // the offsets baked into the matrix, measured at the same instant —
    // callers must strip these, not their own (possibly lagging) copies
    pageOffsetX,
    yOffset,
  };
};
